import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@capacit-com/lara-law-shared-ui";
import { ChevronDown, ChevronRight, ExternalLink, Info } from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@capacit-com/lara-law-shared-ui";
import {
  Switch,
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import useDocumentLink from "@/hooks/useDocumentLink";
import {
  DocumentLink,
  RefType,
  ExtraMetadata,
} from "@/models/document-link/DocumentLink";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import {
  NEW_TAB_MODIFIER_TOOLTIP,
  openAppNavigation,
  type NavigationContext,
} from "@/lib/navigation/appNavigation";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import { useReferenceSidebarHoverPreviewStore } from "@/stores/reference-sidebar-hover-preview.store";
import { useReferencesSidebarFiltersStore } from "@/stores/references-sidebar-filters.store";
import { getDisplayName } from "@/config/datasources";
import {
  getDocumentTypeName,
  getDocumentTypePopularName,
} from "@/utils/documentTypeMapping";
import { documentLinkMatchesUrlHighlight } from "@/utils/documentLinkMatchesUrlHighlight";
import {
  buildLegislationReferenceGroups,
  countLegislationReferenceLinks,
  type LegislationReferenceGroups,
  type ReferenceLegislationGroup,
} from "@/utils/documentLinkReferenceLegislationGroups";
import {
  buildDocumentViewerQueryString,
  parseDocumentViewerHighlightParams,
  REF_SCROLL_QUERY,
  removeReferenceHighlightParamsFromSearchParams,
} from "@/partials/document-renderer/utils/referenceHighlightQuery";
import { cn } from "@/lib/utils";
import {
  getOtherSideWorkspaceName,
  isOtherSideEarlierWorkspaceVersion,
  isOtherSideHistorical,
} from "@/utils/documentLinkOtherSide";
import { parseReferencesSidebarExtraMetadata as parseExtraMetadata } from "@/utils/documentLinkReferencesSidebarVisibility";
import {
  buildWorkspaceReferenceVisibilityContext,
  filterWorkspaceReferenceLinksForDisplay,
  type WorkspaceReferenceVisibilityContext,
} from "@/utils/documentLinkWorkspaceVersionCollapse";
import CreateDocumentReferenceButton from "@/components/document-link/CreateDocumentReferenceButton";
import type { DocumentReferenceTarget } from "@/lib/documentReferenceRequest";

/** True when the document viewer is legislation HTML (not workspace). */
function isViewingLegislationDocumentPath(pathname: string): boolean {
  if (
    /\/workspaces\/[^/]+\/document\//i.test(pathname) ||
    /\/chat\/workspace\/[^/]+\/document\//i.test(pathname)
  ) {
    return false;
  }
  return /\/legislation\/[^/]+\/datasource\//i.test(pathname);
}

/** True when the document viewer is a workspace document (sidebar references apply). */
function isViewingWorkspaceDocumentPath(pathname: string): boolean {
  return (
    /\/workspaces\/[^/]+\/document\//i.test(pathname) ||
    /\/chat\/workspace\/[^/]+\/document\//i.test(pathname)
  );
}

function hasNonEmptyDestinationHtmlIds(link: DocumentLink): boolean {
  return Boolean(link.destinationHtmlIds?.some((id) => id?.trim()));
}

type DocumentReferencesTabProps = {
  documentId: string;
  /** Current revision version number being viewed (for filtering links from non-current revisions) */
  currentRevisionVersion?: number;
  /** Enables "Opret reference" for the open document. Omit to keep the tab read-only. */
  createReferenceTarget?: DocumentReferenceTarget;
};

/** Other end of the link when viewing outgoing (destination) or incoming (origin). */
type OtherSideKind = "legislation" | "workspace";

function referenceTypeLabel(refType?: RefType | string): string | undefined {
  if (!refType) return undefined;
  switch (refType) {
    case RefType.INTERNAL:
      return "Intern";
    case RefType.TEXT_LEVEL_TO_TEXT_LEVEL:
      return "Tekst til tekst";
    case RefType.TEXT_LEVEL_TO_DOCUMENT:
      return "Tekst til dokument";
    case RefType.DOCUMENT_TO_DOCUMENT:
      return "Dokument til dokument";
    case RefType.DOCUMENT_TO_TEXT_LEVEL:
      return "Dokument til tekst";
    case RefType.CASE_HISTORY:
      return "Sagshistorik";
    default:
      return undefined;
  }
}

function classifyOtherSide(
  link: DocumentLink,
  isOutgoing: boolean
): OtherSideKind {
  if (isOutgoing) {
    return link.destinationWorkspaceId ? "workspace" : "legislation";
  }
  return link.originWorkspaceId ? "workspace" : "legislation";
}

/** Backend-resolved title for the document on the other end of the reference. */
function otherSideDocumentName(
  link: DocumentLink,
  isOutgoing: boolean
): string | undefined {
  const raw = isOutgoing
    ? link.destinationDocumentName
    : link.originDocumentName;
  const t = raw?.trim();
  return t || undefined;
}

/** Anchor / matched text on the open document (origin when outgoing, destination when incoming). */
function currentDocumentUniqueMatchText(
  link: DocumentLink,
  isOutgoing: boolean
): string | undefined {
  const raw = isOutgoing
    ? link.originUniqueMatchText
    : link.destinationUniqueMatchText;
  const t = raw?.trim();
  return t || undefined;
}

/** Chunk / disambiguation text on the open document (paired with match text). */
function currentDocumentChunkText(
  link: DocumentLink,
  isOutgoing: boolean
): string | undefined {
  const raw = isOutgoing
    ? link.originUniqueChunkText
    : link.destinationUniqueChunkText;
  const t = raw?.trim();
  return t || undefined;
}

/** HTML anchor ids on the open document for the reference (when match text is absent, block highlight can still apply). */
function currentDocumentHtmlIds(
  link: DocumentLink,
  isOutgoing: boolean
): string[] | undefined {
  const raw = isOutgoing ? link.originHtmlIds : link.destinationHtmlIds;
  const ids = raw?.filter((id): id is string => Boolean(id?.trim()));
  return ids && ids.length > 0 ? ids : undefined;
}

const MATCH_TEXT_TOOLTIP_MAX = 100;

function truncateMatchTextForTooltip(text: string): string {
  if (text.length <= MATCH_TEXT_TOOLTIP_MAX) return text;
  return `${text.slice(0, MATCH_TEXT_TOOLTIP_MAX)} (...)`;
}

function isOutgoingLink(link: DocumentLink, documentId: string): boolean {
  return link.originDocumentId === documentId;
}

function legislationTagLabels(
  link: DocumentLink,
  isOutgoing: boolean,
  metadata: ExtraMetadata
): { sourceLabel: string | undefined; documentTypeLabel: string | undefined } {
  const rawSource = isOutgoing
    ? link.destinationSource?.trim()
    : link.originSource?.trim();
  const sourceLabel =
    (rawSource && (getDisplayName(rawSource) ?? rawSource)) || undefined;

  const docTypeId = isOutgoing
    ? metadata.destination_document_type_id
    : metadata.origin_document_type_id;
  const documentTypeLabel =
    getDocumentTypePopularName(docTypeId) ??
    getDocumentTypeName(docTypeId) ??
    undefined;

  return { sourceLabel, documentTypeLabel };
}

/** Non-historical first; stable order within each bucket. */
function sortLinksForDisplay(
  links: DocumentLink[],
  documentId: string,
  workspaceVisibilityContext?: WorkspaceReferenceVisibilityContext
): DocumentLink[] {
  return [...links].sort((a, b) => {
    const isStaleA =
      isOtherSideHistorical(a, documentId) ||
      isOtherSideEarlierWorkspaceVersion(
        a,
        documentId,
        workspaceVisibilityContext
      );
    const isStaleB =
      isOtherSideHistorical(b, documentId) ||
      isOtherSideEarlierWorkspaceVersion(
        b,
        documentId,
        workspaceVisibilityContext
      );
    if (isStaleA !== isStaleB) {
      return isStaleA ? 1 : -1;
    }
    const sortKey = (link: DocumentLink) => {
      const o = isOutgoingLink(link, documentId);
      const meta = parseExtraMetadata(link.extraMetadata);
      const short = o ? meta.destination_short_name : meta.origin_short_name;
      return (
        otherSideDocumentName(link, o) ||
        short?.trim() ||
        (link.name ?? "").trim()
      );
    };
    return sortKey(a).localeCompare(sortKey(b), "da", { sensitivity: "base" });
  });
}

const emptyLegislationReferenceGroups = (): LegislationReferenceGroups => ({
  headerGroups: [],
  typeGroups: [],
});

const REFERENCES_FILTER_SHOW_HISTORICAL_TOOLTIP =
  "Viser referencer til ophævede eller erstattede love og bekendtgørelser fra Retsinformation — som på retsinformation.dk. Gælder ikke workspace-dokumenter.";

const REFERENCES_FILTER_SHOW_EARLIER_WORKSPACE_TOOLTIP =
  "Viser referencer til ældre revisioner af workspace-dokumenter. Når slået fra, vises kun den aktive revision af hvert dokument.";

type ReferencesFilterToggleRowProps = {
  id: string;
  label: string;
  tooltip: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
};

const ReferencesFilterToggleRow: FC<ReferencesFilterToggleRowProps> = ({
  id,
  label,
  tooltip,
  checked,
  onCheckedChange,
}) => (
  <div className="flex items-center justify-between gap-2">
    <label
      htmlFor={id}
      className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-xs text-sidebar-foreground/70"
    >
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
      <span>{label}</span>
    </label>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          id={`${id}-info`}
          className="shrink-0 cursor-help rounded-sm p-0.5 text-sidebar-foreground/50 transition-colors hover:text-sidebar-foreground/85 outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-label={`Forklaring: ${label}`}
        >
          <Info className="size-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-[280px]">
        <p className="text-xs leading-snug">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </div>
);

type ReferenceDirectionSectionProps = {
  documentId: string;
  prefix: "fra" | "til";
  title: string;
  tooltip: string;
  legislationGroups: LegislationReferenceGroups;
  workspaceLinks: DocumentLink[];
  renderSubsection: (
    triggerId: string,
    openKey: string,
    label: string,
    subsectionLinks: DocumentLink[]
  ) => ReactNode;
};

/** Heading row (tooltip anchored to full width; open only via info icon) + legislation groups + workspace list. */
const ReferenceDirectionSection: FC<ReferenceDirectionSectionProps> = ({
  documentId,
  prefix,
  title,
  tooltip,
  legislationGroups,
  workspaceLinks,
  renderSubsection,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  useEffect(() => {
    setTooltipOpen(false);
  }, [documentId]);

  const total =
    countLegislationReferenceLinks(legislationGroups) + workspaceLinks.length;
  if (total === 0) return null;

  const renderGroup = (g: ReferenceLegislationGroup, keyPrefix: string) => (
    <div key={`${prefix}-${keyPrefix}-${g.groupKey}`}>
      {renderSubsection(
        `references-section-${prefix}-${keyPrefix}-${g.groupKey}-trigger`,
        `${prefix}-${keyPrefix}-${g.groupKey}`,
        g.label,
        g.links
      )}
    </div>
  );

  return (
    <div className="space-y-1">
      <Tooltip
        open={tooltipOpen}
        onOpenChange={(next) => {
          if (!next) setTooltipOpen(false);
        }}
      >
        <TooltipTrigger asChild>
          <div className="flex w-full items-center justify-between gap-2 px-2 pt-1">
            <p
              id={`references-section-refererer-${prefix}-heading`}
              className="min-w-0 text-left text-[11px] font-semibold uppercase tracking-wide text-sidebar-foreground/70"
            >
              {title}
            </p>
            <button
              type="button"
              id={`references-section-refererer-${prefix}-info`}
              className="shrink-0 cursor-help rounded-sm p-0.5 text-sidebar-foreground/50 transition-colors hover:text-sidebar-foreground/85 outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label={`Forklaring: ${title}`}
              onPointerEnter={() => setTooltipOpen(true)}
              onPointerLeave={() => setTooltipOpen(false)}
            >
              <Info className="size-3.5" aria-hidden />
            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[280px]">
          <p className="text-xs leading-snug">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
      <div className="border-l border-sidebar-border/60 ml-2 pl-2 space-y-0">
        {legislationGroups.headerGroups.map((g) => renderGroup(g, "hdr"))}
        {legislationGroups.typeGroups.map((g) => renderGroup(g, "type"))}
        {workspaceLinks.length > 0
          ? renderSubsection(
              `references-section-${prefix}-ws-trigger`,
              `${prefix}-ws`,
              "Workspaces",
              workspaceLinks
            )
          : null}
      </div>
    </div>
  );
};

const DocumentReferencesTab: FC<DocumentReferencesTabProps> = ({
  documentId,
  currentRevisionVersion,
  createReferenceTarget,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { useQueryByDocumentId } = useDocumentLink();
  const { resetWorkspaceDocumentVersionStore } =
    useWorkspaceDocumentVersionStore();

  const beginHoverPreview = useReferenceSidebarHoverPreviewStore(
    (s) => s.beginHover
  );
  const endHoverPreview = useReferenceSidebarHoverPreviewStore((s) => s.endHover);
  const endContentHover = useReferenceSidebarHoverPreviewStore(
    (s) => s.endContentHover
  );
  const contentHoverLinkIds = useReferenceSidebarHoverPreviewStore(
    (s) => s.contentHoverLinkIds
  );
  const hoverLeaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    endHoverPreview();
    endContentHover();
  }, [documentId, endHoverPreview, endContentHover]);

  useEffect(() => {
    return () => {
      if (hoverLeaveTimeoutRef.current) {
        clearTimeout(hoverLeaveTimeoutRef.current);
      }
      endHoverPreview();
      endContentHover();
    };
  }, [endHoverPreview, endContentHover]);

  const isChatRoute = location.pathname.includes("/chat/");
  const navigationContext: NavigationContext = isChatRoute ? "chat" : "app";
  const viewingLegislationDocument = useMemo(
    () => isViewingLegislationDocumentPath(location.pathname),
    [location.pathname]
  );
  const viewingWorkspaceDocument = useMemo(
    () => isViewingWorkspaceDocumentPath(location.pathname),
    [location.pathname]
  );
  const showHistorical = useReferencesSidebarFiltersStore(
    (s) => s.showHistorical
  );
  const setShowHistorical = useReferencesSidebarFiltersStore(
    (s) => s.setShowHistorical
  );
  const showEarlierWorkspaceVersions = useReferencesSidebarFiltersStore(
    (s) => s.showEarlierWorkspaceVersions
  );
  const setShowEarlierWorkspaceVersions = useReferencesSidebarFiltersStore(
    (s) => s.setShowEarlierWorkspaceVersions
  );
  const [openSubsections, setOpenSubsections] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    setOpenSubsections({});
  }, [documentId]);

  const { data: links, isLoading, error } = useQueryByDocumentId(documentId);

  const {
    incomingLegislationGroups,
    incomingWorkspaceLinks,
    outgoingLegislationGroups,
    outgoingWorkspaceLinks,
  } = useMemo(() => {
    const empty = {
      incomingLegislationGroups: emptyLegislationReferenceGroups(),
      incomingWorkspaceLinks: [] as DocumentLink[],
      outgoingLegislationGroups: emptyLegislationReferenceGroups(),
      outgoingWorkspaceLinks: [] as DocumentLink[],
    };
    if (!links) return empty;

    const filtered = links.filter((link) => {
      const touchesOrigin = link.originDocumentId === documentId;
      const touchesDest = link.destinationDocumentId === documentId;
      if (!touchesOrigin && !touchesDest) return false;

      const metadata = parseExtraMetadata(link.extraMetadata);
      if (metadata.ref_type === RefType.INTERNAL) return false;

      if (!showHistorical && isOtherSideHistorical(link, documentId)) {
        return false;
      }

      // Always filter out links from non-current revisions
      // This handles the case where API returns links from multiple revisions of the current document
      if (currentRevisionVersion !== undefined) {
        const isOutgoing = touchesOrigin;
        const linkRevision = isOutgoing ? link.originVersion : link.destinationVersion;
        if (linkRevision !== undefined && linkRevision !== currentRevisionVersion) {
          return false;
        }
      }

      return true;
    });

    const incomingLeg: DocumentLink[] = [];
    const incomingWs: DocumentLink[] = [];
    const outgoingLeg: DocumentLink[] = [];
    const outgoingWs: DocumentLink[] = [];

    for (const link of filtered) {
      const isOutgoing = isOutgoingLink(link, documentId);
      const otherKind = classifyOtherSide(link, isOutgoing);
      if (isOutgoing) {
        if (otherKind === "workspace") {
          outgoingWs.push(link);
        } else {
          outgoingLeg.push(link);
        }
      } else if (otherKind === "workspace") {
        incomingWs.push(link);
      } else {
        incomingLeg.push(link);
      }
    }

    return {
      incomingLegislationGroups: buildLegislationReferenceGroups(
        incomingLeg,
        documentId,
        false,
        sortLinksForDisplay
      ),
      incomingWorkspaceLinks: sortLinksForDisplay(incomingWs, documentId),
      outgoingLegislationGroups: buildLegislationReferenceGroups(
        outgoingLeg,
        documentId,
        true,
        sortLinksForDisplay
      ),
      outgoingWorkspaceLinks: sortLinksForDisplay(outgoingWs, documentId),
    };
  }, [links, documentId, showHistorical, currentRevisionVersion]);

  const workspaceReferenceVisibilityContext = useMemo(
    () =>
      buildWorkspaceReferenceVisibilityContext(
        [...incomingWorkspaceLinks, ...outgoingWorkspaceLinks],
        documentId
      ),
    [incomingWorkspaceLinks, outgoingWorkspaceLinks, documentId]
  );

  const { incomingWorkspaceDisplay, outgoingWorkspaceDisplay } = useMemo(() => {
    const incoming = filterWorkspaceReferenceLinksForDisplay(
      incomingWorkspaceLinks,
      documentId,
      showEarlierWorkspaceVersions,
      workspaceReferenceVisibilityContext
    );
    const outgoing = filterWorkspaceReferenceLinksForDisplay(
      outgoingWorkspaceLinks,
      documentId,
      showEarlierWorkspaceVersions,
      workspaceReferenceVisibilityContext
    );
    return {
      incomingWorkspaceDisplay: sortLinksForDisplay(
        incoming,
        documentId,
        workspaceReferenceVisibilityContext
      ),
      outgoingWorkspaceDisplay: sortLinksForDisplay(
        outgoing,
        documentId,
        workspaceReferenceVisibilityContext
      ),
    };
  }, [
    incomingWorkspaceLinks,
    outgoingWorkspaceLinks,
    documentId,
    showEarlierWorkspaceVersions,
    workspaceReferenceVisibilityContext,
  ]);

  const urlHighlight = useMemo(
    () => parseDocumentViewerHighlightParams(searchParams),
    [searchParams]
  );

  const matchedSubsectionKeys = useMemo(() => {
    const keys: string[] = [];
    const matches = (list: DocumentLink[]) =>
      list.some((link) =>
        documentLinkMatchesUrlHighlight(
          link,
          documentId,
          urlHighlight.highlightIds,
          urlHighlight.refMatchText,
          urlHighlight.refChunkText
        )
      );
    for (const g of incomingLegislationGroups.headerGroups) {
      if (matches(g.links)) keys.push(`fra-hdr-${g.groupKey}`);
    }
    for (const g of incomingLegislationGroups.typeGroups) {
      if (matches(g.links)) keys.push(`fra-type-${g.groupKey}`);
    }
    if (matches(incomingWorkspaceDisplay)) keys.push("fra-ws");
    for (const g of outgoingLegislationGroups.headerGroups) {
      if (matches(g.links)) keys.push(`til-hdr-${g.groupKey}`);
    }
    for (const g of outgoingLegislationGroups.typeGroups) {
      if (matches(g.links)) keys.push(`til-type-${g.groupKey}`);
    }
    if (matches(outgoingWorkspaceDisplay)) keys.push("til-ws");
    return keys;
  }, [
    incomingLegislationGroups,
    incomingWorkspaceDisplay,
    outgoingLegislationGroups,
    outgoingWorkspaceDisplay,
    documentId,
    urlHighlight.highlightIds,
    urlHighlight.refMatchText,
    urlHighlight.refChunkText,
  ]);

  useEffect(() => {
    if (matchedSubsectionKeys.length === 0) return;
    setOpenSubsections((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const k of matchedSubsectionKeys) {
        if (!next[k]) {
          next[k] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [matchedSubsectionKeys]);

  /** Navigate to the other document (same as previous whole-row click). */
  const handleNavigateToOtherSide = (
    link: DocumentLink,
    event?: React.MouseEvent
  ) => {
    try {
      const opened = openAppNavigation({
        target: {
          kind: "documentLink",
          link,
          currentDocumentId: documentId,
          context: navigationContext,
        },
        navigate,
        event,
        onWorkspaceDocumentNavigate: resetWorkspaceDocumentVersionStore,
      });
      if (!opened) {
        logger.warn("Could not resolve where a document link points", {
          operation: "openReferencedDocument",
          linkId: link.id,
          documentId,
        });
        showErrorToast("Kunne ikke bestemme dokument type");
      }
    } catch (error) {
      logger.error("Failed to navigate to referenced document", {
        operation: "openReferencedDocument",
        error,
        documentId,
        linkId: link.id,
      });
      showErrorToast("Kunne ikke navigere til dokumentet");
    }
  };

  /** Scroll/highlight the current document to this reference’s anchor/match (URL update). */
  const handleScrollToMatchInCurrentDocument = (link: DocumentLink) => {
    const o = isOutgoingLink(link, documentId);
    const htmlIds = currentDocumentHtmlIds(link, o);
    const matchText = currentDocumentUniqueMatchText(link, o)?.trim();
    const chunkText = currentDocumentChunkText(link, o)?.trim();

    const hasIds = Boolean(htmlIds?.length);
    const hasMatch = Boolean(matchText);
    const hasChunk = Boolean(chunkText);

    // Document-only references: nothing to scroll/highlight in the viewer — ignore click (no error).
    if (!hasIds && !hasMatch && !hasChunk) {
      return;
    }

    if (
      documentLinkMatchesUrlHighlight(
        link,
        documentId,
        urlHighlight.highlightIds,
        urlHighlight.refMatchText,
        urlHighlight.refChunkText
      )
    ) {
      endHoverPreview();
      const nextSearch = removeReferenceHighlightParamsFromSearchParams(
        new URLSearchParams(searchParams)
      );
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch,
          hash: location.hash,
        },
        { replace: true }
      );
      return;
    }

    const qs = buildDocumentViewerQueryString({
      htmlIds: hasIds ? htmlIds : undefined,
      matchText: matchText || undefined,
      chunkText: chunkText || undefined,
    });
    const sp = new URLSearchParams(qs.replace(/^\?/, ""));
    sp.set(REF_SCROLL_QUERY, String(Date.now()));

    navigate(
      {
        pathname: location.pathname,
        search: `?${sp.toString()}`,
        hash: location.hash,
      },
      { replace: true }
    );
  };

  const renderLinkItem = (link: DocumentLink) => {
    const isOutgoing = isOutgoingLink(link, documentId);
    const name = link.name?.trim();
    const documentVersion = isOutgoing
      ? link.destinationVersion?.toString()
      : link.originVersion?.toString();

    const metadata = parseExtraMetadata(link.extraMetadata);
    const shortName = isOutgoing
      ? metadata.destination_short_name
      : metadata.origin_short_name;
    const popularTitle = isOutgoing
      ? metadata.destination_popular_title
      : metadata.origin_popular_title;
    const title = isOutgoing
      ? metadata.destination_title
      : metadata.origin_title;

    const isHistorical =
      isOtherSideHistorical(link, documentId) ||
      isOtherSideEarlierWorkspaceVersion(
        link,
        documentId,
        workspaceReferenceVisibilityContext
      );

    const serverDocName = otherSideDocumentName(link, isOutgoing);
    const displayName =
      serverDocName || shortName?.trim() || name || undefined;
    const fallbackTitle = (popularTitle || title)?.trim();
    const currentDocMatchText = currentDocumentUniqueMatchText(
      link,
      isOutgoing
    );
    const extendedTitle =
      fallbackTitle && (!displayName || fallbackTitle !== displayName)
        ? fallbackTitle
        : undefined;
    const refTypeLabel = referenceTypeLabel(metadata.ref_type);
    const matchTextForTooltip = currentDocMatchText
      ? truncateMatchTextForTooltip(currentDocMatchText)
      : undefined;

    const refTypeDisplay = refTypeLabel?.trim();
    const extendedDisplay = extendedTitle?.trim();
    const matchDisplay = (matchTextForTooltip ?? "").trim();
    const showDocumentReferenceNoMatchHint =
      viewingLegislationDocument && !currentDocMatchText;
    const showWorkspaceDestinationDocumentRefHint =
      viewingWorkspaceDocument &&
      !isOutgoing &&
      !hasNonEmptyDestinationHtmlIds(link);
    const showDocumentReferenceHint =
      showDocumentReferenceNoMatchHint ||
      showWorkspaceDestinationDocumentRefHint;

    const otherSideKind = classifyOtherSide(link, isOutgoing);
    const otherIsLegislation = otherSideKind === "legislation";
    const otherSideWorkspaceName =
      otherSideKind === "workspace"
        ? getOtherSideWorkspaceName(link, documentId)
        : undefined;

    const hasReferenceTooltipContent = Boolean(
      refTypeDisplay ||
        extendedDisplay ||
        matchDisplay ||
        showDocumentReferenceHint ||
        (otherSideKind === "workspace" && otherSideWorkspaceName)
    );
    const { sourceLabel, documentTypeLabel } = otherIsLegislation
      ? legislationTagLabels(link, isOutgoing, metadata)
      : { sourceLabel: undefined, documentTypeLabel: undefined };

    const nameElement = displayName ? (
      <span className="font-medium text-xs leading-tight wrap-break-word">
        {`${displayName}${documentVersion ? ` v${documentVersion}` : ""}`}
      </span>
    ) : (
      <span className="font-medium text-xs leading-tight wrap-break-word italic">
        Unavngivet
      </span>
    );

    const legislationSourceLine = otherIsLegislation
      ? sourceLabel && documentTypeLabel
        ? `${sourceLabel} (${documentTypeLabel})`
        : sourceLabel
          ? sourceLabel
          : documentTypeLabel
            ? `(${documentTypeLabel})`
            : ""
      : "";

    const referenceRowInner = (
      <div className="flex flex-col flex-1 gap-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {nameElement}
          {isHistorical && (
            <Badge
              variant="secondary"
              className="text-[10px] px-1.5 py-0 h-4 font-normal"
            >
              Historisk
            </Badge>
          )}
        </div>
        {legislationSourceLine ? (
          <p className="text-[10px] text-muted-foreground leading-snug wrap-break-word pr-1">
            {legislationSourceLine}
          </p>
        ) : null}
      </div>
    );

    const handleReferencePointerEnter = () => {
      if (hoverLeaveTimeoutRef.current) {
        clearTimeout(hoverLeaveTimeoutRef.current);
        hoverLeaveTimeoutRef.current = null;
      }
      const o = isOutgoingLink(link, documentId);
      beginHoverPreview(
        documentId,
        currentDocumentUniqueMatchText(link, o),
        currentDocumentChunkText(link, o),
        currentDocumentHtmlIds(link, o)
      );
    };

    const handleReferencePointerLeave = () => {
      hoverLeaveTimeoutRef.current = setTimeout(() => {
        endHoverPreview();
        hoverLeaveTimeoutRef.current = null;
      }, 280);
    };

    const isActiveFromReferenceNavigation =
      documentLinkMatchesUrlHighlight(
        link,
        documentId,
        urlHighlight.highlightIds,
        urlHighlight.refMatchText,
        urlHighlight.refChunkText
      );

    const isHighlightedFromDocumentHover =
      contentHoverLinkIds.length > 0 &&
      contentHoverLinkIds.includes(link.id);

    const referenceScrollToMatchButton = (
      <SidebarMenuButton
        id={`reference-scroll-to-match-${link.id}`}
        type="button"
        className={cn(
          "group/ref-item h-auto min-w-0 flex-1 cursor-default select-none text-left items-start",
          "bg-transparent hover:bg-transparent active:bg-transparent",
          "focus-visible:bg-transparent data-[state=open]:bg-transparent"
        )}
        onClick={() => handleScrollToMatchInCurrentDocument(link)}
      >
        {referenceRowInner}
      </SidebarMenuButton>
    );

    return (
      <SidebarMenuItem key={link.id}>
        <div
          data-reference-sidebar-row
          className={cn(
            "group/ref-row flex w-full min-w-0 cursor-default items-stretch gap-0.5 rounded-md px-1 -mx-1 py-0.5 transition-colors",
            "hover:bg-sidebar-accent/50",
            (isActiveFromReferenceNavigation ||
              isHighlightedFromDocumentHover) &&
              "bg-sidebar-accent/50 ring-1 ring-inset ring-primary/30"
          )}
          data-reference-from-navigation={
            isActiveFromReferenceNavigation ? "true" : undefined
          }
          data-reference-highlighted-from-document={
            isHighlightedFromDocumentHover ? "true" : undefined
          }
          onPointerEnter={handleReferencePointerEnter}
          onPointerLeave={handleReferencePointerLeave}
        >
          {hasReferenceTooltipContent ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {referenceScrollToMatchButton}
              </TooltipTrigger>
              <TooltipContent
                side="left"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                className="select-text"
              >
                <div className="max-w-[300px] space-y-2">
                  {refTypeDisplay && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-background/75">
                        Referencetype
                      </p>
                      <p className="text-xs">{refTypeDisplay}</p>
                    </div>
                  )}
                  {otherSideKind === "workspace" && otherSideWorkspaceName ? (
                    <div
                      className={cn(
                        refTypeDisplay && "border-t border-background/20 pt-2"
                      )}
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-background/75">
                        Workspace
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-xs">
                        {otherSideWorkspaceName}
                      </p>
                    </div>
                  ) : null}
                  {extendedDisplay && (
                    <div
                      className={cn(
                        (refTypeDisplay ||
                          (otherSideKind === "workspace" &&
                            otherSideWorkspaceName)) &&
                          "border-t border-background/20 pt-2"
                      )}
                    >
                      {otherSideKind === "legislation" ? (
                        <>
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-background/75">
                            Lovtitel
                          </p>
                          <p className="mt-1 whitespace-pre-wrap text-xs">
                            {extendedDisplay}
                          </p>
                        </>
                      ) : (
                        <p className="whitespace-pre-wrap">{extendedDisplay}</p>
                      )}
                    </div>
                  )}
                  {showDocumentReferenceHint && (
                    <div
                      className={cn(
                        (refTypeDisplay ||
                          extendedDisplay ||
                          (otherSideKind === "workspace" &&
                            otherSideWorkspaceName)) &&
                          "border-t border-background/20 pt-2"
                      )}
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-background/75">
                        Dokumentreference
                      </p>
                      <p className="mt-1 text-xs">
                        Denne reference er lavet ud fra dokumentet og ikke en
                        bestemt tekst i dokumentet.
                      </p>
                    </div>
                  )}
                  {matchDisplay && (
                    <div
                      className={cn(
                        (refTypeDisplay ||
                          extendedDisplay ||
                          showDocumentReferenceHint ||
                          (otherSideKind === "workspace" &&
                            otherSideWorkspaceName)) &&
                          "border-t border-background/20 pt-2"
                      )}
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-background/75">
                        Tekst referencen vedrører
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-xs">
                        {matchDisplay}
                      </p>
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          ) : (
            referenceScrollToMatchButton
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                id={`reference-go-to-link-${link.id}`}
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto shrink-0 cursor-pointer self-start rounded-md border border-transparent px-1.5 py-1 text-muted-foreground transition-[border-color,colors] hover:border-sidebar-border hover:bg-transparent hover:text-sidebar-accent-foreground"
                aria-label="Gå til dokument"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigateToOtherSide(link, e);
                }}
              >
                <ExternalLink className="size-3.5" aria-hidden />
                <span className="sr-only">Gå til dokument</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Gå til dokument</p>
              <p className="text-xs text-background/75">{NEW_TAB_MODIFIER_TOOLTIP}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </SidebarMenuItem>
    );
  };

  // Stays outside the loading / error / empty branches below: the moment a
  // document has no references yet is exactly when this action is needed.
  const createReferenceHeader = createReferenceTarget ? (
    <div className="px-2 py-2 border-b border-sidebar-border shrink-0">
      <CreateDocumentReferenceButton target={createReferenceTarget} />
    </div>
  ) : null;

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        {createReferenceHeader}
        <div className="flex flex-1 items-center justify-center w-full p-4">
          <p className="text-sm text-sidebar-foreground">
            Indlæser referencer...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        {createReferenceHeader}
        <div className="flex flex-1 items-center justify-center w-full p-4">
          <p className="text-sm text-sidebar-foreground text-center">
            Fejl ved indlæsning af referencer
          </p>
        </div>
      </div>
    );
  }

  if (!links || links.length === 0) {
    return (
      <div className="flex flex-col h-full">
        {createReferenceHeader}
        <div className="flex flex-1 items-center justify-center w-full p-4">
          <p className="text-sm text-sidebar-foreground w-[80%] text-center">
            Ingen dokumentreferencer tilgængelige
          </p>
        </div>
      </div>
    );
  }

  const totalFiltered =
    countLegislationReferenceLinks(incomingLegislationGroups) +
    incomingWorkspaceDisplay.length +
    countLegislationReferenceLinks(outgoingLegislationGroups) +
    outgoingWorkspaceDisplay.length;

  const renderCollapsibleSubsection = (
    triggerId: string,
    openKey: string,
    label: string,
    subsectionLinks: DocumentLink[]
  ) => (
    <Collapsible
      open={openSubsections[openKey] ?? false}
      onOpenChange={(next) =>
        setOpenSubsections((prev) => ({ ...prev, [openKey]: next }))
      }
      className="mb-2 last:mb-0"
    >
      <CollapsibleTrigger
        id={triggerId}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-xs font-semibold text-sidebar-foreground/80 hover:bg-sidebar-accent/30 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
      >
        {openSubsections[openKey] ? (
          <ChevronDown className="size-3.5 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="size-3.5 shrink-0" aria-hidden />
        )}
        <span className="min-w-0 flex-1">
          {label} ({subsectionLinks.length})
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {subsectionLinks.length === 0 ? (
          <p className="px-2 pb-2 pt-0.5 text-xs text-sidebar-foreground/60">
            Ingen
          </p>
        ) : (
          <SidebarMenu>
            {subsectionLinks.map((link) => renderLinkItem(link))}
          </SidebarMenu>
        )}
      </CollapsibleContent>
    </Collapsible>
  );

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {createReferenceHeader}
      <div className="px-2 py-2 border-b border-sidebar-border shrink-0 flex flex-col gap-2">
        <ReferencesFilterToggleRow
          id="historical-toggle-references"
          label="Vis historiske"
          tooltip={REFERENCES_FILTER_SHOW_HISTORICAL_TOOLTIP}
          checked={showHistorical}
          onCheckedChange={setShowHistorical}
        />
        <ReferencesFilterToggleRow
          id="earlier-workspace-versions-toggle-references"
          label="Vis tidligere versioner"
          tooltip={REFERENCES_FILTER_SHOW_EARLIER_WORKSPACE_TOOLTIP}
          checked={showEarlierWorkspaceVersions}
          onCheckedChange={setShowEarlierWorkspaceVersions}
        />
      </div>
      <SidebarGroup className="min-h-0 flex-1">
        <SidebarGroupContent className="px-0">
          {totalFiltered === 0 ? (
            <div className="px-2 py-4 text-center">
              <p className="text-xs text-sidebar-foreground/70">
                {showHistorical
                  ? "Ingen referencer"
                  : "Ingen ikke-historiske referencer"}
              </p>
            </div>
          ) : (
            <div className="space-y-5 pb-4">
              <ReferenceDirectionSection
                documentId={documentId}
                prefix="til"
                title="Refererer til"
                tooltip="Dokumenter (lovgivning og workspace), som det åbne dokument refererer til."
                legislationGroups={outgoingLegislationGroups}
                workspaceLinks={outgoingWorkspaceDisplay}
                renderSubsection={renderCollapsibleSubsection}
              />
              <ReferenceDirectionSection
                documentId={documentId}
                prefix="fra"
                title="Refererer fra"
                tooltip="Dokumenter (lovgivning og workspace), der har en reference til det dokument, du har åbnet."
                legislationGroups={incomingLegislationGroups}
                workspaceLinks={incomingWorkspaceDisplay}
                renderSubsection={renderCollapsibleSubsection}
              />
            </div>
          )}
        </SidebarGroupContent>
      </SidebarGroup>
    </div>
  );
};

export default DocumentReferencesTab;
